import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus, faCopy, faCheck, faPaperPlane, faBan,
    faClock, faCircleCheck, faHourglassHalf, faCommentDots
} from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import InviteExternalModal from './InviteExternalModal';
import ExternalAccessEditor from './ExternalAccessEditor';
import AccessExpiry from './AccessExpiry';
import { onSocket } from '../../utils/socket';

/**
 * The external participants of one group, inside the details pane
 * (feature draft F2.4, F2.6, and the request-to-join half of F2.2).
 *
 * Every member can see this list, not just the people who can manage it.
 * Knowing an outsider is in the room changes what you say in it, so hiding
 * that from ordinary members would defeat the purpose of announcing it in the
 * thread at all — the actions are what is gated, not the knowledge.
 */

const STATUS = {
    invited: { label: 'Invited', icon: faClock, tone: 'wait' },
    pending: { label: 'Waiting for approval', icon: faHourglassHalf, tone: 'wait' },
    active: { label: 'Active', icon: faCircleCheck, tone: 'ok' },
    declined: { label: 'Declined', icon: faBan, tone: 'off' },
    revoked: { label: 'Access withdrawn', icon: faBan, tone: 'off' }
};

const when = (iso) => (iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '');

/**
 * canManage  group admins (and Admin/HR): everything, including access edits.
 * canInvite  any member: may bring a guest in, and look after the guests they
 *            invited themselves - resend, copy the link, withdraw.
 */
const ExternalParticipants = ({ conversation, canManage, canInvite = canManage }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviting, setInviting] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [editingId, setEditingId] = useState(null);

    const conversationId = conversation._id;
    const navigate = useNavigate();
    const [opening, setOpening] = useState(null);

    // A one-to-one chat with this guest (guest role), made on first use.
    const messageGuest = async (p) => {
        setOpening(p._id);
        try {
            const res = await api.post('/conversations/guest-direct', { externalUserId: p.externalUser });
            navigate(`/chats/${res.data._id}`);
        } catch (err) {
            Swal.fire('Could not open the chat', err.response?.data?.message || 'Please try again.', 'error');
        } finally {
            setOpening(null);
        }
    };
    const me = JSON.parse(localStorage.getItem('user') || 'null');
    const myId = String(me?.id || me?._id || '');
    const invitedByMe = (p) => String(p.invitedBy?._id || p.invitedBy || '') === myId;

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/conversations/${conversationId}/externals`);
            setRows(res.data);
        } catch {
            // A group with no external access is the normal case and the panel
            // should not shout about it; an error here just means an empty list.
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [conversationId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const timer = setInterval(load, 30000);
        return () => clearInterval(timer);
    }, [load]);
    useEffect(() => onSocket('external:updated', load), [load]);

    const copyLink = (p) => {
        navigator.clipboard?.writeText(p.link);
        setCopiedId(p._id);
        setTimeout(() => setCopiedId(null), 1800);
    };

    const resend = async (p) => {
        try {
            const res = await api.post(`/conversations/${conversationId}/externals/${p._id}/resend`);
            await load();
            Swal.fire({
                icon: res.data.emailed ? 'success' : 'warning',
                title: res.data.emailed ? 'Invitation resent' : 'Could not email it',
                text: res.data.emailed
                    ? `A fresh link is on its way to ${p.email}.`
                    : 'The link has been renewed — copy it and send it another way.',
                timer: res.data.emailed ? 1800 : undefined,
                showConfirmButton: !res.data.emailed
            });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not resend.', 'error');
        }
    };

    const decide = async (p, approve) => {
        try {
            await api.post(`/conversations/${conversationId}/externals/${p._id}/${approve ? 'approve' : 'decline'}`);
            await load();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not save that.', 'error');
        }
    };

    const revoke = async (p) => {
        const ok = await Swal.fire({
            title: `Withdraw access for ${p.name}?`,
            text: 'Their link stops working immediately. What they have already said stays in the conversation.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Withdraw access'
        });
        if (!ok.isConfirmed) return;

        try {
            await api.delete(`/conversations/${conversationId}/externals/${p._id}`);
            await load();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not withdraw access.', 'error');
        }
    };

    if (loading) return null;

    // Nothing to show and nothing you could do about it.
    if (!rows.length && !canInvite) return null;

    const live = rows.filter((p) => ['invited', 'pending', 'active'].includes(p.status));

    return (
        <div className="msgr-info-section">
            <h5>
                Guests
                {live.length > 0 && <span className="ext-count">{live.length}</span>}
            </h5>

            {canInvite && (
                <button
                    className="msgr-btn block"
                    style={{ marginBottom: 10 }}
                    onClick={() => setInviting(true)}
                >
                    <FontAwesomeIcon icon={faUserPlus} /> Add a guest
                </button>
            )}

            {!rows.length && (
                <p className="ext-hint" style={{ margin: 0 }}>
                    Nobody outside the company can see this group.
                </p>
            )}

            {rows.map((p) => {
                const s = STATUS[p.status] || STATUS.invited;
                // Only an invitation not yet accepted runs out; a joined guest
                // keeps access until somebody withdraws it.
                const joined = p.status === 'active';
                const expired = !joined && !['revoked', 'declined'].includes(p.status) && new Date(p.expiresAt) <= new Date();

                return (
                    <div className="ext-row" key={p._id}>
                        <div className="ext-row-head">
                            <div className="ext-row-id">
                                <span className="ext-badge">Guest</span>
                                <span className="ext-row-name">{p.name}</span>
                            </div>
                            <span className={`ext-status ${expired ? 'off' : s.tone}`}>
                                <FontAwesomeIcon icon={expired ? faClock : s.icon} />
                                {expired ? 'Access expired' : s.label}
                            </span>
                        </div>

                        <div className="ext-row-meta">
                            {p.company && <span>{p.company}</span>}
                            <span>{p.email}</span>
                            <span>{p.externalId}</span>
                            {p.type && <span>{p.type.toLowerCase()}</span>}
                        </div>

                        <div className="ext-row-meta">
                            <span>
                                Invited by {p.invitedBy?.name || 'someone'} on {when(p.invitedAt)}
                            </span>
                            {joined ? <span>Access until removed</span> : <>
                                <span>Invitation valid until {new Date(p.expiresAt).toLocaleString()}</span>
                                {!['revoked', 'declined'].includes(p.status) && <AccessExpiry expiresAt={p.expiresAt} />}
                            </>}
                            <span>{p.role === 'viewer' ? 'View only' : 'Can reply and upload'}</span>
                            <span>{p.shareHistory ? 'Full chat history' : 'Messages since invitation'}</span>
                        </div>

                        <details className="ext-access-history">
                            <summary>Access history</summary>
                            <ul>
                                <li>Invited {new Date(p.invitedAt).toLocaleString()} by {p.invitedBy?.name || 'a group admin'}</li>
                                {p.joinedAt && <li>Joined {new Date(p.joinedAt).toLocaleString()}</li>}
                                {(p.accessHistory || []).filter(event => event.action !== 'invited').map((event, index) => (
                                    <li key={event._id || index}>
                                        {new Date(event.at).toLocaleString()} — {event.action}
                                        {event.expiresAt && `; expires ${new Date(event.expiresAt).toLocaleString()}`}
                                        {event.role && `; ${event.role === 'viewer' ? 'view only' : 'can reply and upload'}`}
                                        {typeof event.shareHistory === 'boolean' && `; ${event.shareHistory ? 'full history' : 'history since invitation'}`}
                                    </li>
                                ))}
                                {p.revokedAt && <li>Removed {new Date(p.revokedAt).toLocaleString()}</li>}
                            </ul>
                        </details>

                        {canManage && editingId === p._id && <ExternalAccessEditor participant={p} conversationId={conversationId}
                            onSaved={() => { setEditingId(null); load(); }} onCancel={() => setEditingId(null)} />}

                        {/* Anyone in the group can message a joined guest one-to-one -
                            but not from inside that one-to-one chat itself. */}
                        {p.status === 'active' && p.personActive !== false && !conversation.isGuestDirect && (
                            <div className="ext-row-actions">
                                <button className="msgr-btn" disabled={opening === p._id} onClick={() => messageGuest(p)}>
                                    <FontAwesomeIcon icon={faCommentDots} /> {opening === p._id ? 'Opening…' : 'Message'}
                                </button>
                            </div>
                        )}

                        {/* Nothing to manage in a one-to-one guest chat: it stays
                            open until the guest is switched off. */}
                        {(canManage || invitedByMe(p)) && !conversation.isGuestDirect && (
                            <div className="ext-row-actions">
                                {canManage && !['revoked', 'declined'].includes(p.status) && <button className="msgr-btn" onClick={() => setEditingId(p._id)}>Edit access</button>}
                                {p.status === 'pending' && (
                                    <>
                                        <button className="msgr-btn primary" onClick={() => decide(p, true)}>
                                            Approve
                                        </button>
                                        <button className="msgr-btn" onClick={() => decide(p, false)}>
                                            Decline
                                        </button>
                                    </>
                                )}

                                {p.link && p.status !== 'revoked' && (
                                    <button className="msgr-btn" onClick={() => copyLink(p)}>
                                        <FontAwesomeIcon icon={copiedId === p._id ? faCheck : faCopy} />
                                        {copiedId === p._id ? 'Copied' : 'Copy link'}
                                    </button>
                                )}

                                {p.status !== 'revoked' && (
                                    <button className="msgr-btn" onClick={() => resend(p)}>
                                        <FontAwesomeIcon icon={faPaperPlane} />
                                        {expired ? 'Renew & resend' : 'Resend'}
                                    </button>
                                )}

                                {p.status !== 'revoked' && p.status !== 'declined' && (
                                    <button className="msgr-btn danger" onClick={() => revoke(p)}>
                                        <FontAwesomeIcon icon={faBan} /> Withdraw
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {inviting && (
                <InviteExternalModal
                    conversation={conversation}
                    canShareHistory={canManage}
                    /* So the picker can grey out somebody already in this
                       group rather than letting the invitation fail on submit. */
                    existingIds={live.map((p) => p.externalUser).filter(Boolean)}
                    onClose={() => { setInviting(false); load(); }}
                    onInvited={load}
                />
            )}
        </div>
    );
};

export default ExternalParticipants;
